import dynamic from 'next/dynamic';

import TestimonialRating from './components/TestimonialRating';
import SocialReview from './components/SocialReview';
import MoreReviews from './components/MoreReviews';
import AutoplayCarousel from './components/AutoplayCarousel';

import { useTextFontOverrides, useHeadingFontOverrides } from 'utils/font-override';
import { buildCssVar } from 'utils/style-override';

import type { ComponentsTestimonial } from 'queries/graphql';

const MarkdownText = dynamic(() => import('generic/MarkdownText'));

import styles from './styles.module.scss';

export default function Testimonial({
  sectionId,
  tagline,
  title,
  ratings,
  text,
  socialReviews,
  moreReviews,
  style,
}: ComponentsTestimonial) {
  const {
    taglineColor,
    headingColor,
    textColor,
    backgroundColor,
    ratingsBackgroundColor,
    headingFont,
    textFont,
    // @lkuich - These are supposed to be passed down to child components?
    // itemHeadingFont,
    // itemTextFont,
    moreReviewStyle,
    ratingStyle,
    socialReviewStyle,
  } = style || {};

  const headingFontOverrides = useHeadingFontOverrides(headingFont);
  const textFontOverrides = useTextFontOverrides(textFont);

  const rootClassNames = ['root', styles.container];
  if (backgroundColor) rootClassNames.push(styles.top_bottom_padding);

  return (
    <div id={sectionId} className={`${rootClassNames.join(' ')} testimonial`}>
      <style jsx>{`
        .root {
          ${buildCssVar('--tagline-color', taglineColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--heading-color', headingColor, 'var(--heading-color-dark)')}
          ${buildCssVar('--text-color', textColor, 'var(--text-color-dark)')}
          ${buildCssVar('--background-color', backgroundColor, 'var(--default-background-color)')}
          ${buildCssVar(
            '--ratings-background-color',
            ratingsBackgroundColor,
            'var(--default-background-color)'
          )}
          ${headingFontOverrides ?? ''}
          ${textFontOverrides ?? ''}
        }
      `}</style>

      <div className={styles.testimonial_section}>
        <div className={styles.sec_wrapper}>
          {tagline && <p className={styles.tagline}>{tagline}</p>}
          {title && <h1 className={styles.testimonial_heading}>{title}</h1>}
          {text && (
            <div className={styles.testimonial_text}>
              <MarkdownText text={text} />
            </div>
          )}

          {!!ratings.length && (
            <div className={styles.ratings_wrapper}>
              {ratings.map(item => (
                <TestimonialRating key={item.id} {...item} ratingStyle={ratingStyle} />
              ))}
            </div>
          )}

          {!!socialReviews.length && (
            <div className={styles.testimonial_social_wrapper}>
              <AutoplayCarousel
                showButtons
                shouldLoop
                shouldAutoplay={false}
                alignment="start"
                items={socialReviews.map((review, i) => (
                  <div key={i} className={styles.carousel_item_wrapper}>
                    <SocialReview
                      key={review.id}
                      {...review}
                      socialReviewStyle={socialReviewStyle}
                    />
                  </div>
                ))}
              />
            </div>
          )}

          {moreReviews && <MoreReviews {...moreReviews} moreReviewStyle={moreReviewStyle} />}
        </div>
      </div>
    </div>
  );
}
